/**
 * User Moderation Preferences (localStorage-backed)
 *
 * Persists per-user preferences for:
 *  - Hidden posts (post IDs the user doesn't want to see)
 *  - Muted users  (user IDs whose posts should be filtered out)
 *
 * Keys are namespaced by the current Sharetribe user ID so that
 * multiple users on the same browser don't collide.
 */

const HIDDEN_POSTS_KEY = 'riticket_hidden_posts';
const MUTED_USERS_KEY = 'riticket_muted_users';

// ─── Helpers ────────────────────────────────────────────────────────────────

const readSet = key => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const writeSet = (key, set) => {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch (e) {
    console.warn('[userModerationPrefs] localStorage write failed:', e);
  }
};

// ─── Hidden Posts ────────────────────────────────────────────────────────────

export const getHiddenPosts = () => readSet(HIDDEN_POSTS_KEY);

export const hidePost = postId => {
  const set = getHiddenPosts();
  set.add(String(postId));
  writeSet(HIDDEN_POSTS_KEY, set);
};

export const unhidePost = postId => {
  const set = getHiddenPosts();
  set.delete(String(postId));
  writeSet(HIDDEN_POSTS_KEY, set);
};

export const isPostHidden = postId => getHiddenPosts().has(String(postId));

// ─── Muted Users ─────────────────────────────────────────────────────────────

export const getMutedUsers = () => readSet(MUTED_USERS_KEY);

export const muteUser = userId => {
  const set = getMutedUsers();
  set.add(String(userId));
  writeSet(MUTED_USERS_KEY, set);
};

export const unmuteUser = userId => {
  const set = getMutedUsers();
  set.delete(String(userId));
  writeSet(MUTED_USERS_KEY, set);
};

export const isUserMuted = userId => getMutedUsers().has(String(userId));

// ─── Composite filter ────────────────────────────────────────────────────────

/**
 * Returns a filter function that can be passed to Array.prototype.filter
 * to remove hidden posts and posts from muted users.
 *
 *   const visible = posts.filter(moderationFilter());
 */
export const moderationFilter = () => {
  const hidden = getHiddenPosts();
  const muted = getMutedUsers();

  return post => {
    if (hidden.has(String(post.id))) return false;
    const authorId = post.authorId || post.user_id || post.actor?.id;
    if (authorId && muted.has(String(authorId))) return false;
    return true;
  };
};
